import clsx from 'clsx';
import { NoDataMessage } from '../../../components/NoDataMessage';
import { DynamicSkeleton } from '../../../components/DynamicSkeleton';
import { Table } from '../../../components/Table';
import { SupportTicket, TableColumn } from '../../../types/commonTypes';
import { Tag } from '../../../components/Tag';

interface Props {
  isMobile: boolean;
  loading: boolean;
  onSelectedTicket(ticket: SupportTicket): void;
  pendingOnly: boolean;
  selectedTicket?: SupportTicket;
  tickets: SupportTicket[];
}

export const ViewTicketsTable = ({
  isMobile,
  loading,
  onSelectedTicket,
  pendingOnly,
  selectedTicket,
  tickets,
}: Props) => {
  const columns: TableColumn[] = [
    {
      name: 'Status',
      dataKey: 'behavior',
      width: 100,
      isHidden: isMobile,
    },
    {
      name: 'Subject',
      dataKey: 'subject',
      width: 400,
      minWidth: 400,
      isHidden: false,
    },
    {
      name: 'Ticket#',
      order: 1,
      dataKey: 'displayID',
      width: 125,
      isHidden: false,
    },
    {
      name: 'Type',
      dataKey: 'categoryName',
      width: 150,
      isHidden: isMobile,
    },
    {
      name: 'Created On',
      dataKey: 'createdAt',
      width: 200,
      isHidden: isMobile,
    },
    {
      name: 'Last Update',
      dataKey: 'lastUpdatedAt',
      width: 200,
      isHidden: isMobile,
    },
    {
      name: 'Last Update By',
      dataKey: 'lastUpdatedBy',
      width: 250,
      isHidden: isMobile,
    },
    {
      name: 'Priority',
      dataKey: 'priority',
      width: 100,
      isHidden: true,
    },
  ];

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center w-full py-4 px-12">
        <div className="flex w-full">
          <NoDataMessage title="Loading Tickets..." />
        </div>
        <DynamicSkeleton
          direction="column"
          spacing={2}
          rowHeight={30}
          skeletons={['100%', '100%', '100%', '100%', '100%']}
        />
      </div>
    );
  else if (!loading && tickets !== undefined && tickets.length > 0)
    return (
      <Table
        customTool={
          pendingOnly ? (
            <span className="px-1">All Pending Tickets</span>
          ) : undefined
        }
        height={isMobile ? 460 : 400}
        columns={columns}
        data={tickets}
        loading={loading}
        csvName="TicketData.csv"
        singleSelect
        selectedItems={selectedTicket ? [selectedTicket] : undefined}
        onClick={onSelectedTicket}
        disableToolbar={isMobile}
        showTotalRows
        dataRenderProps={[
          {
            id: 'categoryName',
            component: (value) => (
              <Tag
                className={clsx('mx-auto', {
                  'bg-[#EABEC0] text-[#9E1921]':
                    value.toLowerCase() === 'incident',
                  'bg-[#BDDDD5] text-[#074F3E]':
                    value.toLowerCase() === 'request' ||
                    value.toLowerCase() === 'question',
                  'bg-[#D2B7D8] text-[#681A78]':
                    value.toLowerCase() !== 'question' &&
                    value.toLowerCase() !== 'request' &&
                    value.toLowerCase() !== 'incident',
                })}
                square
              >
                {value}
              </Tag>
            ),
          },
          {
            id: 'behavior',
            component: (value) => (
              <Tag
                className={clsx({
                  'bg-[#A3C7CF] text-[#0A4C5B]':
                    value.toLowerCase() !== 'closed',
                  'bg-gray-200 text-gray-800': value.toLowerCase() === 'closed',
                })}
                square
              >
                {value}
              </Tag>
            ),
          },
        ]}
      />
    );
  else
    return (
      <div className="w-full h-full p-4">
        <NoDataMessage
          title="There are no open tickets to display."
          subTitle="Enable 'View All' to filter open/closed tickets by date range."
        />
      </div>
    );
};
